import RichText from '@/components/RichText'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { formatNumber } from '@/utilities/formatNumber'
import { ArrowBigRight, Users } from 'lucide-react'
import React from 'react'

const FaqSection2 = ({
  questions,
  title,
  customerCount,
  addSchema = false,
  images,
  subtitle,
}: {
  questions: any
  title: any
  customerCount: number
  addSchema?: boolean
  images?: any
  subtitle?: any
}) => {
  // Helper function to extract plain text from richText for schema
  const extractTextContent = (content: any): string => {
    if (typeof content === 'string') return content
    if (content && typeof content === 'object' && content.root) {
      try {
        const extractText = (node: any): string => {
          if (typeof node === 'string') return node
          if (node?.text) return node.text
          if (node?.children) {
            return node.children.map(extractText).join('')
          }
          return ''
        }
        return extractText(content)
      } catch {
        return ''
      }
    }
    return ''
  }

  // Generate FAQPage JSON-LD schema
  const faqSchema = addSchema
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: questions?.map((item: any) => ({
          '@type': 'Question',
          name: extractTextContent(item.question),
          acceptedAnswer: {
            '@type': 'Answer',
            text: extractTextContent(item.answer),
          },
        })),
      }
    : null
  return (
    <main className="container mx-auto flex flex-col min-h-[50vh]" aria-label="FAQ">
      {addSchema && faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      <section className="w-full grid grid-cols-1 lg:grid-cols-2 gap-10" role="list">
        {/* FAQ Accordion Column */}
        <section className="flex flex-col" aria-label="FAQ List">
          <header className="text-left mb-4">
            {title && (
              <RichText
                className="border border-border px-4 text-xs py-1 rounded-full mb-2 w-fit flex items-center justify-center "
                data={title}
                enableGutter={false}
                enableProse={true}
              />
            )}
            {subtitle && (
              <RichText
                className="text-xl md:text-2xl font-light text-muted-foreground mb-4"
                data={subtitle}
                enableGutter={false}
                enableProse={true}
              />
            )}
          </header>
          <Accordion type="single" collapsible className="w-full grid grid-cols-1 gap-6 ">
            {questions?.map((item: any, index: any) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-none flex flex-col h-full"
                role="li"
              >
                <AccordionTrigger
                  className="relative cursor-pointer pl-[30px] pr-[9px] py-[19px] border rounded-[31px] transition-all duration-200 delay-100 flex items-center justify-between [&[data-state=open]>span]:rotate-90  [&[data-state=open]>span]:bg-primary"
                  hideIcon={true}
                  asDiv={true}
                >
                  {item.question && (
                    <RichText
                      className="m-0 text-[16px] font-bold leading-[24px] transition-all duration-500"
                      data={item.question}
                      enableGutter={false}
                      enableProse={true}
                    />
                  )}
                  <span className="flex items-center justify-center w-[50px] h-[50px] text-[16px] font-bold text-center rounded-full transition-all duration-500 bg-slate-400 text-primary">
                    <ArrowBigRight className="text-white transition-transform duration-200" />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="mx-8 my-8 text-muted-foreground flex-1 flex flex-col">
                  {/* Example: Add internal anchor link to a service area page */}
                  {/* <a href="/services/fencing#dallas">Learn more about Dallas Fencing</a> */}
                  {item.answer && (
                    <RichText
                      className="mb-0"
                      data={item.answer}
                      enableGutter={false}
                      enableProse={true}
                    />
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Images and Customer Count Column */}
        <aside
          className="relative mt-[30px] hidden lg:block"
          aria-label="FAQ Images and Customer Count"
        >
          <div className="flex flex-wrap h-full">
            {/* Left Image and Count */}
            <div className="w-full md:w-1/2 pr-[13px] flex flex-col h-full">
              <figure className="relative mb-[26px]">
                <img
                  src={images?.[0]?.image?.url}
                  alt="Access Control Systems North Dallas Fence Company"
                  className="w-full picture-rounded"
                />
              </figure>
              {!!customerCount && (
                <section
                  className="relative z-[2] flex items-center bg-destructive rounded-[20px] px-[60px] py-[38px] mt-[26px] ml-[49px] mr-[-91px] group"
                  aria-label="Customer Count"
                >
                  <span className="relative inline-block group/icon">
                    <span className="text-[40px] text-white transform transition-transform duration-500  group-hover/icon:scale-[0.9]">
                      <Users className="h-14 w-14" />
                    </span>
                  </span>
                  <div className="ml-[40px]">
                    <div className="flex items-center">
                      <h3 className="text-[59px] font-bold leading-[59px] tracking-[-1.2px] text-white">
                        {formatNumber(customerCount)}+
                      </h3>
                    </div>
                    <p className="mt-[2px] text-[16px] font-bold text-white">Trusted Customer</p>
                  </div>
                </section>
              )}
            </div>
            {/* Right Image */}
            <div className="w-full md:w-1/2 pl-[13px] flex flex-col h-full">
              <figure className="relative h-full">
                <img
                  src={images?.[1]?.image?.url}
                  alt="Agricultural Fence Solutions in North Dallas"
                  className="w-full h-[55vh] picture-rounded object-cover"
                />
              </figure>
            </div>
          </div>
        </aside>
      </section>
    </main>
  )
}

export default FaqSection2
